import React, { useMemo } from 'react';

import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { Listing } from '@fastlessjs/backoffice-layer/dist/components/templates/Listing';
import { useMultiTenancy } from '@fastlessjs/backoffice-layer/dist/hooks';
import { featureVoter } from '@fastlessjs/backoffice-layer/dist/services';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Space } from 'antd';
import { usePath } from 'hookrouter-react';

import EurekaRefreshButton from '../../components/EurekaRefreshButton';
import { t } from '../../config/translations';
import { useSuivi } from '../hooks';
import { traceSchema } from '../schemas';
import { Trace } from '../types';
import SearchSuiviModal from './SearchSuiviModal';
import { getTraceListItemPath, isPendingTrace } from '../utils/traceNavigation';
import { resolveProcessingStatus } from '../utils/traceStatusDisplay';

debug.registerComponent('ShowTrace');

const getOrgFromPath = (path: string): string => path.split('/').filter(Boolean)[0] ?? '';

const TraceListExtraButtons = () => {
  const [path] = usePath();
  const orgFromPath = getOrgFromPath(path);
  const { getPage } = useMultiTenancy();
  const enterpriseId = getPage('enterprises')?.[orgFromPath]?.enterpriseId;
  const enterpriseIdNum =
    enterpriseId !== undefined && enterpriseId !== null ? Number(enterpriseId) : NaN;
  const canRefreshFromEureka = Number.isFinite(enterpriseIdNum);

  return (
    <Space size="small">
      <SearchSuiviModal org={orgFromPath} enterpriseId={enterpriseId} />
      <EurekaRefreshButton
        entity="suivi"
        filter={{ enterpriseId: enterpriseIdNum }}
        queryKeysToInvalidate={[['suivis', enterpriseId]]}
        disabled={!canRefreshFromEureka}
      />
    </Space>
  );
};

type TraceListExtraActionsProps = {
  record?: Trace;
  organizationId?: string;
};

const TraceListExtraActions = ({
  record,
  organizationId,
}: TraceListExtraActionsProps) => {
  const org = organizationId ?? '';
  const { getPage } = useMultiTenancy();
  const enterpriseId = getPage('enterprises')?.[org]?.enterpriseId;

  if (isPendingTrace(record)) {
    return <span />;
  }

  return (
    <EurekaRefreshButton
      mode="row"
      entity="suivi"
      items={[{ suiviId: record?.suiviId }]}
      queryKeysToInvalidate={[['suivis', enterpriseId]]}
      disabled={!record?.eurekaNsui}
    />
  );
};

const ShowTrace = ({ org }: PageProps) => {
  debug.trackRender('ShowTrace');
  debug.log('ShowTrace', `Rendering with org: ${org}`);
  const { getPage } = useMultiTenancy();
  const enterpriseId = getPage('enterprises')?.[org]?.enterpriseId;
  const enterpriseName = getPage('enterprises')?.[org]?.label;

  const options = useMemo(() => {
    if (featureVoter('traceList:admins')) {
      return {
        tenantId: enterpriseId,
        endpoint: '/admins/suivi',
      };
    }

    if (featureVoter('traceList:clients')) {
      return { tenantId: enterpriseId, endpoint: '/clients/suivi' };
    }

    return { tenantId: enterpriseId };
  }, [enterpriseId]);

  return (
    <Listing<Trace>
      title={t('page.tracking')}
      name="suivi"
      subTitle={`${enterpriseName} - ${enterpriseId}`}
      idKey="suiviId"
      org={org!}
      schema={traceSchema()}
      options={options}
      rowKey={(record: Trace) => record?.suiviId || Math.random()}
      fetchHook={useSuivi as any}
      transformData={(rows: Trace[]) =>
        rows.map((row) => ({
          ...row,
          displayReturnStatus: row.returnStatus,
          displayProcessingStatus: resolveProcessingStatus(row),
        }))
      }
      enableSelection={false}
      hasNew={false}
      hasEdit={false}
      hasRefresh={false}
      ExtraButtons={TraceListExtraButtons}
      ExtraActions={TraceListExtraActions}
      itemUrl={(args) => ({ view: getTraceListItemPath(args?.record || {}) })}
    />
  );
};

export default ShowTrace;

debug.markInitialized('ShowTrace');
